/**
 * FoodPulseDealDetail — single deal page with live countdown, vendor
 * info, and a message board ("Is this still available?" / vendor reply).
 *
 * Route: /food-pulse/deals/:slug
 * Reads:  GET /api/deals/{slug}
 *         GET /api/deals/{slug}/messages
 * Writes: POST /api/deals/{slug}/messages (auth)
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Clock, MapPin, Tag, Send, ArrowLeft, Store, Heart, ShoppingCart, Camera, ShieldCheck, ThumbsUp, ThumbsDown, Share2, Phone, Mail, MessageCircle, Sprout } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";
import AboutThisFoodCard from "@/components/AboutThisFoodCard";

const API = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

function ContactWidget({ deal }) {
  if (deal.source !== "producer") return null;
  const wa = deal.contact_whatsapp;
  const ph = deal.contact_phone;
  const em = deal.contact_email;
  if (!wa && !ph && !em) return null;
  const waHref = wa ? `https://wa.me/${wa.replace(/[^\d+]/g, "").replace(/^\+/, "")}` : null;
  return (
    <div className="rounded-2xl border p-4 mb-4" style={{ borderColor: "#E9D8B4", background: "#fff" }} data-testid="deal-contact-widget">
      <div className="flex items-center gap-2 mb-3">
        <Sprout className="w-4 h-4" style={{ color: GREEN }} />
        <strong className="text-sm">Direct contact</strong>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest opacity-60">no middlemen</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {waHref && (
          <a href={waHref} target="_blank" rel="noreferrer"
             className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white"
             style={{ background: "#25D366" }}
             data-testid="deal-contact-whatsapp">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
        )}
        {ph && (
          <a href={`tel:${ph}`}
             className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white"
             style={{ background: GREEN }}
             data-testid="deal-contact-call">
            <Phone className="w-4 h-4" /> Call {ph}
          </a>
        )}
        {em && (
          <a href={`mailto:${em}`}
             className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border-2"
             style={{ borderColor: GREEN, color: GREEN, background: "#fff" }}
             data-testid="deal-contact-email">
            <Mail className="w-4 h-4" /> {em}
          </a>
        )}
      </div>
      {deal.delivery_radius_km && (
        <div className="mt-2 text-xs opacity-70">
          🚗 Will deliver within {deal.delivery_radius_km} km
        </div>
      )}
    </div>
  );
}

function VerifyWidget({ deal, onChange }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const myVote = (deal.verifications || []).find((v) => v.user_id === user?.id);
  const score = deal.verification_score || 0;
  const sendVote = async (status) => {
    if (!user) return alert("Sign in to verify.");
    setBusy(true);
    try {
      await http.post(`/deals/${deal.slug}/verify`, { status });
      onChange?.();
    } catch (e) { alert(e?.response?.data?.detail || "Verify failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border p-4 mb-4" style={{ borderColor: "#E9D8B4", background: "#fff" }} data-testid="deal-verify-widget">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4" style={{ color: GREEN }} />
        <strong className="text-sm">Community verification</strong>
        <span className={`ml-auto text-[11px] font-mono rounded-full px-2 py-0.5`}
              style={{ background: score > 0 ? "#F0F5F1" : score < 0 ? "#FEE2E2" : "#F5F5F5",
                       color: score > 0 ? GREEN : score < 0 ? "#B91C1C" : "#666" }}>
          score {score >= 0 ? `+${score}` : score}
        </span>
      </div>
      <p className="text-xs opacity-70 mb-3">Help other shoppers — is this deal still there right now?</p>
      <div className="flex gap-2 flex-wrap">
        <button disabled={busy} onClick={() => sendVote("still_available")}
                className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border-2`}
                style={{ borderColor: myVote?.status === "still_available" ? GREEN : "#E9D8B4",
                         background: myVote?.status === "still_available" ? GREEN : "transparent",
                         color: myVote?.status === "still_available" ? "#fff" : GREEN }}
                data-testid="verify-still-available">
          <ThumbsUp className="w-3 h-3" /> Still available
        </button>
        <button disabled={busy} onClick={() => sendVote("sold_out")}
                className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border-2`}
                style={{ borderColor: myVote?.status === "sold_out" ? "#B91C1C" : "#FCA5A5",
                         background: myVote?.status === "sold_out" ? "#B91C1C" : "transparent",
                         color: myVote?.status === "sold_out" ? "#fff" : "#B91C1C" }}
                data-testid="verify-sold-out">
          <ThumbsDown className="w-3 h-3" /> Sold out
        </button>
        <button disabled={busy} onClick={() => sendVote("price_wrong")}
                className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs border-2 border-amber-300 text-amber-700"
                data-testid="verify-price-wrong">
          Price wrong
        </button>
      </div>
    </div>
  );
}

function ShareWidget({ deal }) {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = `${window.location.origin}/food-pulse/deals/${deal.slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: deal.title, text: `Great deal on FoodPulse: ${deal.title}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true); setTimeout(() => setCopied(false), 2000);
      }
      // record on backend for share count + scout points
      http.post(`/deals/${deal.slug}/share`).catch(() => {});
    } catch { /* user cancelled */ }
  };
  return (
    <button onClick={share}
            className="w-full mb-4 rounded-2xl border py-2.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-black/5"
            style={{ borderColor: "#E9D8B4", color: GREEN, background: "#fff" }}
            data-testid="deal-share-btn">
      <Share2 className="w-4 h-4" /> {copied ? "Link copied!" : `Share this deal${deal.share_count ? ` · ${deal.share_count}` : ""}`}
    </button>
  );
}

function useCountdown(endsAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const end = endsAt ? new Date(endsAt).getTime() : null;
  if (!end) return { expired: false, label: "no expiry set" };
  const diff = end - now;
  if (diff <= 0) return { expired: true, label: "This deal has expired" };
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const urgent = d === 0 && h < 3;
  return { expired: false, label: `${d}d ${h}h ${m}m ${sec}s`, urgent };
}

export default function FoodPulseDealDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  const ctd = useCountdown(deal?.ends_at);

  const loadAll = async () => {
    try {
      const [d, m] = await Promise.all([
        axios.get(`${API}/api/deals/${slug}`),
        axios.get(`${API}/api/deals/${slug}/messages`),
      ]);
      setDeal(d.data);
      setMsgs(m.data.items || []);
    } catch {
      setDeal(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-line */ }, [slug]);

  const post = async () => {
    if (!draft.trim() || posting) return;
    setPosting(true);
    try {
      const r = await http.post(`/deals/${slug}/messages`, { body: draft.trim() });
      setMsgs((prev) => [...prev, r.data]);
      setDraft("");
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not post. Are you signed in?");
    } finally { setPosting(false); }
  };

  const addToWishlist = async () => {
    if (!user) return alert("Please sign in first.");
    if (!deal?.food_item_slug) return alert("This deal isn't linked to the master food catalog yet.");
    try {
      await http.post(`/lists/wishlist`, {
        food_item_slug: deal.food_item_slug,
        wish_price: deal.deal_price,
        notify_on_match: true,
      });
      alert("Added to your wish list — we'll notify you on the next matching deal.");
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not save.");
    }
  };

  if (loading) return <div className="min-h-screen p-24 text-center opacity-60" style={{ background: CREAM }}>Loading deal…</div>;
  if (!deal)   return (
    <div className="min-h-screen p-24 text-center" style={{ background: CREAM }}>
      <div className="font-display text-3xl mb-2">Deal not found</div>
      <Link to="/food-pulse/deals" className="underline text-sm">← Back to today's deals</Link>
    </div>
  );

  const image = deal.image_urls?.[0];
  const savings = deal.original_price && deal.deal_price
    ? Math.round(((deal.original_price - deal.deal_price) / deal.original_price) * 100)
    : deal.discount_pct;

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="deal-detail">
      <SeoHead section="food-pulse-deal" title={`${deal.title} — Food Pulse deal`} description={deal.description || ""} />
      <div className="max-w-5xl mx-auto px-4 pt-6">
        <Link to="/food-pulse/deals" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100" data-testid="deal-back">
          <ArrowLeft className="w-4 h-4" /> All deals
        </Link>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-6 pb-16 grid gap-8 lg:grid-cols-5">
        {/* Left: image + description */}
        <div className="lg:col-span-3">
          <div className="rounded-3xl overflow-hidden aspect-[4/3] mb-6" style={{ background: `linear-gradient(135deg,#F6C87A,${CREAM})` }}>
            {image ? (
              <img src={image} alt={deal.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Tag className="w-24 h-24 opacity-30" style={{ color: GREEN }} />
              </div>
            )}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: AMBER }}>
            {deal.kind === "surplus" ? "Surplus rescue" : deal.kind === "coupon" ? "Coupon" : "Special offer"}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-4">{deal.title}</h1>

          {deal.source === "community_scout" && (
            <div className="mb-4 rounded-2xl p-3 flex items-center gap-3" style={{ background: "#FFF7E4", border: "1px solid #E9D8B4" }} data-testid="deal-scout-attribution">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: AMBER, color: GREEN }}>
                <Camera className="w-5 h-5" />
              </div>
              <div className="text-sm flex-1">
                Spotted by <strong>{deal.scout_name}</strong>
                {deal.claim_status === "claimed" && <span className="ml-2 text-[11px] rounded-full px-2 py-0.5" style={{ background: "#F0F5F1", color: GREEN }}><ShieldCheck className="w-3 h-3 inline" /> Verified by vendor</span>}
                {deal.claim_status === "unclaimed" && <span className="ml-2 text-[11px] rounded-full px-2 py-0.5" style={{ background: "#F0F5F1", color: "#8B5E10" }}>Unclaimed by vendor</span>}
                {deal.claim_status === "pending_vendor" && <span className="ml-2 text-[11px] rounded-full px-2 py-0.5" style={{ background: "#FFE4C4", color: "#8B5E10" }}>Store not registered</span>}
              </div>
            </div>
          )}

          {deal.description && <p className="text-base leading-relaxed opacity-90 whitespace-pre-line">{deal.description}</p>}

          {/* About this food — mini knowledge card linking into the
              product profile's tabbed page. Only renders when the deal
              is linked to a canonical food item in the master catalog. */}
          {deal.food_item_slug && (
            <div className="mt-6">
              <AboutThisFoodCard slug={deal.food_item_slug} />
            </div>
          )}

          {deal.pickup_location && (
            <div className="mt-6 flex items-start gap-3 p-4 rounded-2xl border" style={{ borderColor: AMBER, background: "#FFF7E4" }}>
              <MapPin className="w-5 h-5 mt-0.5" style={{ color: AMBER }} />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest">Pickup</div>
                <div className="font-semibold">{deal.pickup_location}</div>
              </div>
            </div>
          )}
        </div>

        {/* Right: price + countdown + message board */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-3xl border p-6 shadow-lg" style={{ background: "#fff", borderColor: "#E9D8B4" }}>
            {savings ? (
              <div className="inline-block rounded-full px-3 py-1 text-xs font-bold text-white mb-3" style={{ background: "#DC2626" }}>
                −{savings}% off
              </div>
            ) : null}
            {deal.deal_price != null && (
              <div className="flex items-baseline gap-3 mb-1">
                <div className="text-5xl font-bold" style={{ color: GREEN }}>${deal.deal_price}</div>
                {deal.unit && <div className="text-sm opacity-70">per {deal.unit}</div>}
              </div>
            )}
            {deal.original_price && deal.original_price > (deal.deal_price ?? 0) && (
              <div className="text-sm opacity-60 line-through">${deal.original_price}</div>
            )}
            {deal.quantity_available != null && (
              <div className="mt-4 text-sm">
                <span className="font-semibold">{deal.quantity_available}</span> {deal.unit || "units"} available
              </div>
            )}
            <div className={`mt-5 rounded-2xl px-4 py-3 text-center ${ctd.urgent ? "animate-pulse" : ""}`}
                 style={{ background: ctd.expired ? "#EEE" : (ctd.urgent ? "#DC2626" : GREEN), color: ctd.expired ? "#666" : "#fff" }}>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">Ends in</div>
              <div className="font-mono text-xl font-semibold" data-testid="deal-countdown">{ctd.label}</div>
            </div>

            {!ctd.expired && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  onClick={addToWishlist}
                  className="flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border-2 hover:bg-black/5"
                  style={{ borderColor: GREEN, color: GREEN }}
                  data-testid="deal-add-wishlist"
                >
                  <Heart className="w-4 h-4" /> Wishlist
                </button>
                <button
                  onClick={() => alert("Cart flow coming soon. Contact the vendor via the message board for now.")}
                  className="flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white"
                  style={{ background: AMBER, color: GREEN }}
                  data-testid="deal-add-cart"
                >
                  <ShoppingCart className="w-4 h-4" /> Grab this
                </button>
              </div>
            )}
          </div>

          {/* Verify + Share widgets (community-scout deals) */}
          {deal.source === "community_scout" && (
            <VerifyWidget deal={deal} onChange={loadAll} />
          )}
          <ContactWidget deal={deal} />
          <ShareWidget deal={deal} />

          {deal.vendor_name && (
            <div className="rounded-2xl border p-4 flex items-center gap-3" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: GREEN, color: AMBER }}>
                <Store className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">Vendor</div>
                <div className="font-semibold truncate">{deal.vendor_name}</div>
                {(deal.city || deal.state) && (
                  <div className="text-xs opacity-70 truncate">{deal.city}{deal.state ? `, ${deal.state}` : ""}</div>
                )}
              </div>
              {deal.vendor_slug && (
                <Link to={`/food-pulse/businesses/${deal.vendor_slug}`} className="text-xs underline underline-offset-4">Profile</Link>
              )}
            </div>
          )}

          {/* Message board */}
          <div className="rounded-2xl border p-5" style={{ borderColor: "#E9D8B4", background: "#fff" }} data-testid="deal-message-board">
            <div className="flex items-baseline justify-between mb-3">
              <div className="font-display text-xl">Q &amp; A</div>
              <div className="text-xs opacity-60">{msgs.length} message{msgs.length === 1 ? "" : "s"}</div>
            </div>
            <div className="space-y-3 max-h-72 overflow-y-auto mb-3 pr-1">
              {msgs.length === 0 && (
                <div className="text-sm opacity-60 italic">No questions yet — ask the vendor if this is still available.</div>
              )}
              {msgs.map((m) => (
                <div key={m.id} className="rounded-xl p-3" style={{ background: m.author_role === "vendor" ? "#FFF7E4" : "#F0F5F1" }}>
                  <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest opacity-70">
                    <span>{m.author_name}</span>
                    {m.author_role === "vendor" && <span className="rounded-full px-2 py-0.5" style={{ background: AMBER, color: GREEN }}>Vendor</span>}
                    <span className="ml-auto">{new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                  </div>
                  <div className="text-sm mt-1">{m.body}</div>
                </div>
              ))}
            </div>
            {user ? (
              <div className="flex gap-2">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") post(); }}
                  placeholder="Is this still available?"
                  className="flex-1 rounded-full border px-4 py-2 text-sm outline-none focus:border-emerald-800"
                  style={{ borderColor: "#E9D8B4" }}
                  data-testid="deal-message-input"
                />
                <button
                  onClick={post}
                  disabled={!draft.trim() || posting}
                  className="rounded-full px-4 py-2 text-white disabled:opacity-40"
                  style={{ background: GREEN }}
                  data-testid="deal-message-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="text-sm opacity-70 text-center py-2">
                <Link to="/login" className="underline">Sign in</Link> to ask a question.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
